import { ArrowRight, Bot, Command, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { appNavigation } from '@/app/navigation';
import { Input } from '@/components/primitives/input';
import { Modal } from '@/components/primitives/modal';
import { useAssistantStore } from '@/state/assistant-store';
import { useUiStore } from '@/state/ui-store';

export function CommandPalette() {
  const navigate = useNavigate();
  const open = useUiStore((state) => state.commandPaletteOpen);
  const setOpen = useUiStore((state) => state.setCommandPaletteOpen);
  const [query, setQuery] = useState('');
  const runtimeInfo = useAssistantStore((state) => state.runtimeInfo);
  const setAssistantOpen = useAssistantStore((state) => state.setAssistantOpen);

  const actions = useMemo(
    () => [
      ...appNavigation.map((item) => ({
        key: item.to,
        label: item.label,
        description: item.description,
        keywords: item.keywords,
        run: () => navigate(item.to),
        icon: item.icon,
      })),
      {
        key: 'assistant',
        label: 'Open assistant',
        description: 'Launch the global copilot from anywhere in the workspace',
        keywords: ['ai', 'copilot', 'chat'],
        run: () => setAssistantOpen(true),
        icon: Bot,
      },
      {
        key: 'profile',
        label: 'Profile',
        description: 'Open personal profile settings and identity preferences',
        keywords: ['account', 'settings', 'user'],
        run: () => navigate('/profile'),
        icon: Command,
      },
    ],
    [navigate, setAssistantOpen],
  );

  const filteredActions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return actions;
    }

    return actions.filter((action) =>
      [action.label, action.description, ...action.keywords].some((value) => value.toLowerCase().includes(term)),
    );
  }, [actions, query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="Command palette" className="max-w-3xl">
      <div className="space-y-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search navigation, modules, or quick actions..."
            className="pl-10"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {filteredActions.map((action) => (
            <button
              key={action.key}
              type="button"
              className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition hover:border-accent-400/40 hover:bg-accent-500/10"
              onClick={() => {
                setOpen(false);
                action.run();
              }}
              disabled={action.key === 'assistant' && !runtimeInfo?.enabled}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl border border-white/10 bg-ink-900 p-2">
                    <action.icon className="h-4 w-4 text-slate-200" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{action.label}</p>
                    <p className="text-sm text-slate-400">{action.description}</p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-500" />
              </div>
            </button>
          ))}
        </div>

        {!filteredActions.length ? <p className="text-sm text-slate-500">No quick actions match that search.</p> : null}
      </div>
    </Modal>
  );
}
